<template>
  <!-- Modal Backdrop -->
  <div
    v-if="show" 
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    @click="handleBackdropClick"
  >
    <!-- Modal Content -->
    <div 
      class="bg-white rounded-2xl shadow-2xl max-w-sm lg:max-w-md w-full animate__animated animate__zoomIn animate__faster"
      @click.stop
    >
      <!-- Modal Header -->
      <div class="p-4 lg:p-6 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h2 class="text-lg lg:text-xl font-bold text-gray-900">UPI Payment</h2>
          <button 
            @click="$emit('close')"
            class="text-gray-400 hover:text-gray-600 transition-colors lg:p-1"
          >
            <svg class="w-5 h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Modal Body -->
      <div class="p-4 lg:p-6 text-center">
        <!-- Total Amount Display - Main Focus -->
        <div class="mb-6 lg:mb-8">
          <div class="text-4xl lg:text-5xl font-bold text-green-600 mb-1">₹{{ formatAmount(totalAmount) }}</div>
          <p class="text-sm lg:text-base text-gray-500">Scan QR to pay</p>
        </div>

        <!-- QR Code -->
        <div class="flex justify-center mb-4 lg:mb-6">
          <UpiQRCode 
            :key="`qr-${businessVPA}-${totalAmount}-${transactionRef}`"
            :vpa="businessVPA"
            :amount="totalAmount"
            :merchant-name="merchantName"
            :transaction-note="transactionNote"
            :transaction-ref="transactionRef"
            :qr-size="180"
            :qr-size-desktop="220"
          />
        </div>

        <!-- Payment Status -->
        <div v-if="paymentStatus" class="mb-4 lg:mb-6">
          <div 
            class="p-3 lg:p-4 rounded-lg border text-sm lg:text-base"
            :class="paymentStatusClass"
          >
            <div class="flex items-center justify-center">
              <svg 
                class="w-4 h-4 lg:w-5 lg:h-5 mr-2" 
                :class="paymentStatus === 'success' ? 'text-green-600' : paymentStatus === 'error' ? 'text-red-600' : 'text-yellow-600'"
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path 
                  v-if="paymentStatus === 'success'" 
                  fill-rule="evenodd" 
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
                  clip-rule="evenodd"
                />
                <path 
                  v-else-if="paymentStatus === 'error'" 
                  fill-rule="evenodd" 
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" 
                  clip-rule="evenodd"
                />
                <path 
                  v-else 
                  fill-rule="evenodd" 
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" 
                  clip-rule="evenodd"
                />
              </svg>
              <span class="font-medium">{{ paymentStatusMessage }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="p-4 lg:p-6 space-y-3 lg:space-y-4">
        <!-- Primary Action Button -->
        <button
          @click="handlePaymentReceived"
          :disabled="processing"
          class="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 lg:py-4 px-4 lg:px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center lg:text-base"
        >
          <div v-if="processing" class="animate-spin w-4 h-4 lg:w-5 lg:h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
          <svg v-else class="w-5 h-5 lg:w-6 lg:h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
          {{ processing ? 'Processing...' : 'Payment Received' }}
        </button>

        <!-- Secondary Actions -->
        <div class="flex space-x-2">
          <button
            @click="handleChangePaymentMethod"
            :disabled="processing"
            class="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            Change Method
          </button>
          <button
            @click="$emit('close')"
            :disabled="processing"
            class="flex-1 text-gray-600 hover:text-gray-800 font-medium py-2 px-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import UpiQRCode from './UpiQRCode.vue'

// Props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  totalAmount: {
    type: [Number, String],
    required: true
  },
  businessVPA: {
    type: String,
    required: true
  },
  merchantName: {
    type: String,
    default: ''
  },
  transactionNote: {
    type: String,
    default: 'Payment for purchase'
  },
  transactionRef: {
    type: String,
    default: ''
  },
  allowBackdropClose: {
    type: Boolean,
    default: true
  }
})

// Emits
const emit = defineEmits(['close', 'payment-received', 'change-payment-method'])

// Reactive data
const processing = ref(false)
const paymentStatus = ref('') // '', 'success', 'error', 'waiting'
const paymentStatusMessage = ref('')

// Computed
const paymentStatusClass = computed(() => {
  switch (paymentStatus.value) {
    case 'success':
      return 'bg-green-50 border-green-200 text-green-800'
    case 'error':
      return 'bg-red-50 border-red-200 text-red-800'
    case 'waiting':
      return 'bg-yellow-50 border-yellow-200 text-yellow-800'
    default:
      return 'bg-gray-50 border-gray-200 text-gray-800'
  }
})

// Methods
const formatAmount = (amount) => {
  return parseFloat(amount).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const handleBackdropClick = () => {
  if (props.allowBackdropClose && !processing.value) {
    emit('close')
  }
}

const handlePaymentReceived = async () => {
  if (processing.value) return
  
  processing.value = true
  paymentStatus.value = 'waiting'
  paymentStatusMessage.value = 'Processing payment...'
  
  try {
    // Emit payment received event
    emit('payment-received')
    
    // Update status
    paymentStatus.value = 'success'
    paymentStatusMessage.value = 'Payment confirmed successfully!'
    
    // Auto-close after a short delay
    setTimeout(() => {
      emit('close')
    }, 1500)
    
  } catch (error) {
    console.error('Payment processing error:', error)
    paymentStatus.value = 'error'
    paymentStatusMessage.value = 'Failed to process payment. Please try again.'
  } finally {
    processing.value = false
  }
}

const handleChangePaymentMethod = () => {
  if (processing.value) return
  
  emit('change-payment-method')
  emit('close')
}

// Reset status when modal is shown
watch(() => props.show, (newValue) => {
  if (newValue) {
    paymentStatus.value = ''
    paymentStatusMessage.value = ''
    processing.value = false
  }
})
</script>

<style scoped>
/* Additional modal styles */
.modal-backdrop {
  backdrop-filter: blur(2px);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .bg-white {
    max-height: 95vh;
  }
}
</style>